import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { LocalResults } from '../components/search/LocalResults'
import { StoreResults } from '../components/search/StoreResults'
import { Segmented } from '../components/Segmented'
import { useDebounced } from '../hooks/useDebounced'
import { useMeta } from '../hooks/useMeta'

type Source = 'appoutlet' | 'store'

export function SearchScreen() {
	const meta = useMeta()
	const [params, setParams] = useSearchParams()
	const source: Source = params.get('source') === 'store' ? 'store' : 'appoutlet'
	const storefront = params.get('storefront') ?? 'us'
	const [q, setQ] = useState(params.get('q') ?? '')
	const typed = useDebounced(q.trim())
	// The App Store is searched on Return only: Apple's search budget is small.
	const submitted = (params.get('q') ?? '').trim()

	function update(changes: Record<string, string>) {
		const next = { ...Object.fromEntries(params), ...changes }
		setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v)), { replace: true })
	}

	return (
		<div className="stack">
			<div className="page-header row" style={{ justifyContent: 'space-between' }}>
				<h1>Search</h1>
				<div className="row">
					<Segmented<Source>
						label="Search source"
						value={source}
						options={[
							{ value: 'appoutlet', label: 'StoreKeeper' },
							{ value: 'store', label: 'App Store' },
						]}
						onChange={(s) => update({ source: s === 'store' ? 'store' : '' })}
					/>
					{source === 'store' && (
						<select
							aria-label="App Store storefront"
							value={storefront}
							onChange={(e) => update({ storefront: e.target.value })}
						>
							{(meta.data?.storefronts ?? [{ code: 'us', name: 'United States' }]).map((s) => (
								<option key={s.code} value={s.code}>
									{s.code.toUpperCase()} · {s.name}
								</option>
							))}
						</select>
					)}
				</div>
			</div>
			<form
				role="search"
				onSubmit={(e) => {
					e.preventDefault()
					update({ q: q.trim() })
				}}
			>
				<input
					type="search"
					autoFocus
					aria-label="Search query"
					placeholder={
						source === 'store' ? 'Search the App Store, then press Return' : 'App name, developer or App Store id'
					}
					value={q}
					onChange={(e) => {
						setQ(e.target.value)
						if (source === 'appoutlet') update({ q: e.target.value })
					}}
					style={{ width: '100%' }}
				/>
			</form>
			<div className="card" style={{ padding: 0 }}>
				{source === 'store' ? <StoreResults term={submitted} storefront={storefront} /> : <LocalResults term={typed} />}
			</div>
		</div>
	)
}
